/**
 * Geração determinística da arena multiplayer — compartilhada entre servidor e cliente.
 *
 * O servidor é a autoridade: gera os obstáculos a partir do gameSeed e os publica
 * no estado da sala. O cliente usa este mesmo módulo apenas como fallback (caso o
 * estado ainda não tenha chegado) e para manter os tipos alinhados.
 */

export interface SharedObstacle {
  id: string;
  x: number;
  z: number;
  sizeX: number;
  sizeY: number;
  sizeZ: number;
  type: "destructible" | "indestructible";
  health: number;
  maxHealth: number;
}

/** PRNG determinístico (LCG) — mesmo algoritmo usado no gerador de waves do cliente. */
export function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

/**
 * Áreas de nascimento da arena multiplayer: o centro e os oito pontos de borda.
 *
 * Exportado porque duas coisas diferentes precisam da mesma lista — os
 * obstáculos, que não podem tapar um spawn, e o terreno, que não pode afogar
 * um. Enquanto a lista existia só dentro do gerador de obstáculos, o terreno
 * teria de adivinhá-la, e adivinhar errado significa nascer dentro da lava.
 *
 * Os raios escalam com o tamanho da arena — com raio fixo, arenas pequenas
 * (2-4 jogadores) ficavam 100% cobertas por zonas livres e sem obstáculo algum.
 */
export function multiplayerSpawnZones(
  arenaSize: number,
): Array<{ x: number; z: number; radius: number }> {
  const boundary = arenaSize / 2 - 3;
  const centerRadius = Math.min(6, Math.max(2.5, arenaSize * 0.12));
  const edgeRadius = Math.min(4, Math.max(2, arenaSize * 0.08));
  return [
    { x: 0, z: 0, radius: centerRadius },
    { x: -boundary + 3, z: -boundary + 3, radius: edgeRadius },
    { x: boundary - 3, z: -boundary + 3, radius: edgeRadius },
    { x: -boundary + 3, z: boundary - 3, radius: edgeRadius },
    { x: boundary - 3, z: boundary - 3, radius: edgeRadius },
    { x: 0, z: -boundary + 3, radius: edgeRadius },
    { x: 0, z: boundary - 3, radius: edgeRadius },
    { x: -boundary + 3, z: 0, radius: edgeRadius },
    { x: boundary - 3, z: 0, radius: edgeRadius },
  ];
}

/**
 * Gera o layout de obstáculos da arena multiplayer a partir do seed.
 * Determinístico: mesmo seed + mesmo arenaSize ⇒ mesmo layout em todos os peers.
 */
export function generateMultiplayerObstacles(seed: number, arenaSize: number): SharedObstacle[] {
  const random = seededRandom(seed + 10000);
  const obstacles: SharedObstacle[] = [];
  const boundary = arenaSize / 2 - 3;

  const obstaclesDensity = 0.3 + random() * 0.4;
  const hasDestructibles = random() > 0.3;

  const gridSpacing = 4;
  const gridCount = Math.floor(arenaSize / gridSpacing) - 2;
  const offset = ((gridCount - 1) * gridSpacing) / 2;

  const spawnZones = multiplayerSpawnZones(arenaSize);

  const isInSpawnZone = (x: number, z: number) =>
    spawnZones.some((zone) => {
      const dx = x - zone.x;
      const dz = z - zone.z;
      return Math.sqrt(dx * dx + dz * dz) < zone.radius;
    });

  for (let i = 0; i < gridCount; i++) {
    for (let j = 0; j < gridCount; j++) {
      const x = i * gridSpacing - offset;
      const z = j * gridSpacing - offset;

      if (isInSpawnZone(x, z)) continue;
      if (Math.abs(x) >= boundary || Math.abs(z) >= boundary) continue;

      if (random() >= obstaclesDensity) continue;

      const isIndestructible = i % 2 === 1 && j % 2 === 1;
      const isDestructible = hasDestructibles && !isIndestructible && random() > 0.5;

      if (isIndestructible || isDestructible) {
        obstacles.push({
          id: `obs-${i}-${j}`,
          x,
          z,
          sizeX: 2,
          sizeY: isIndestructible ? 2.5 : 1.5,
          sizeZ: 2,
          type: isIndestructible ? "indestructible" : "destructible",
          health: isIndestructible ? 999 : 2,
          maxHealth: isIndestructible ? 999 : 2,
        });
      }
    }
  }

  // Garantia mínima de cobertura: arenas pequenas podem sortear zero obstáculos,
  // deixando o mapa sem cobertura tática. Posições determinísticas ⇒ idênticas
  // em todos os peers.
  if (obstacles.length < 4 && boundary > 3) {
    const d = boundary * 0.55;
    const extraSpots = [
      { x: d, z: 0 },
      { x: -d, z: 0 },
      { x: 0, z: d },
      { x: 0, z: -d },
    ];
    extraSpots.forEach((spot, idx) => {
      if (isInSpawnZone(spot.x, spot.z)) return;
      obstacles.push({
        id: `obs-extra-${idx}`,
        x: spot.x,
        z: spot.z,
        sizeX: 2,
        sizeY: 1.5,
        sizeZ: 2,
        type: "destructible",
        health: 2,
        maxHealth: 2,
      });
    });
  }

  return obstacles;
}

/**
 * Constantes de combate multiplayer — única fonte de verdade para cliente e servidor.
 * O servidor calcula dano/velocidade/tamanho a partir do chargeLevel validado;
 * o cliente usa os mesmos valores para a predição local do próprio tiro.
 */
export const MP_COMBAT = {
  BULLET_SPEED: 30,
  BULLET_DAMAGE: 20,
  BULLET_SIZE: 0.25,
  SHOOT_COOLDOWN_MS: 300,
  /** Tempo (ms) para atingir carga máxima — espelha updateChargeLevel do cliente. */
  CHARGE_TIME_MS: 2000,
  /** damage = base * (1 + charge * CHARGE_DAMAGE_MULT) */
  CHARGE_DAMAGE_MULT: 3,
  /** speed = base * (1 + charge * CHARGE_SPEED_MULT) */
  CHARGE_SPEED_MULT: 0.5,
  /** size = BULLET_SIZE + charge * CHARGE_SIZE_BONUS */
  CHARGE_SIZE_BONUS: 0.5,
  MAX_SHOTS_PER_VOLLEY: 7,
  /** Desvio máximo (rad) de cada tiro do volley em relação ao ângulo principal. */
  MAX_VOLLEY_SPREAD_RAD: 0.5,
} as const;

/** Colisão círculo (tanque/bala) × AABB (obstáculo) no plano XZ. */
export function circleIntersectsObstacle(
  x: number,
  z: number,
  radius: number,
  obs: { x: number; z: number; sizeX: number; sizeZ: number }
): boolean {
  const dx = Math.abs(x - obs.x);
  const dz = Math.abs(z - obs.z);
  return dx < obs.sizeX / 2 + radius && dz < obs.sizeZ / 2 + radius;
}

/**
 * Empurra um círculo para fora de um AABB pelo eixo de menor penetração.
 * Retorna a nova posição (ou a original se não há interseção).
 */
export function resolveCircleObstacleCollision(
  x: number,
  z: number,
  radius: number,
  obs: { x: number; z: number; sizeX: number; sizeZ: number }
): { x: number; z: number } {
  const halfX = obs.sizeX / 2 + radius;
  const halfZ = obs.sizeZ / 2 + radius;
  const dx = x - obs.x;
  const dz = z - obs.z;
  if (Math.abs(dx) >= halfX || Math.abs(dz) >= halfZ) return { x, z };

  const penX = halfX - Math.abs(dx);
  const penZ = halfZ - Math.abs(dz);
  if (penX < penZ) {
    return { x: obs.x + Math.sign(dx || 1) * halfX, z };
  }
  return { x, z: obs.z + Math.sign(dz || 1) * halfZ };
}
